import React from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Body2 } from '@ui/Typography'
import { Loader } from '@ui/components/Spinner/Loader'

import {
  DxTradeInstrumentMarginRate,
  DxTradeInstrumentMarginTier,
  DxTradeTradingHour,
} from '@model/DxTrade.types'

import { Box } from '@shared/components/Box'

import { DAY_TO_INDEX, ParsedEvent, normalizeSchedule } from './hooks/useTradingSessionStatus'

const DAYS_ORDER = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const

type Day = (typeof DAYS_ORDER)[number]

interface MarketTabProps {
  tradingInfo: {
    lotSize: number | null
    minOrderSize: number | null
    maxOrderSize: number | null | undefined
    minOrderSizeIncrement: number | null
    priceIncrement: number | null
    tradingHours: DxTradeTradingHour[] | null
    marginRate: DxTradeInstrumentMarginRate | null
    overnightFees?: any
  } | null
  isLoading: boolean
}

const SECONDS_PER_DAY = 24 * 3600
const SECONDS_PER_WEEK = 7 * SECONDS_PER_DAY

function getLocalOffsetSeconds(): number {
  return -new Date().getTimezoneOffset() * 60
}

interface DayTimeRange {
  openSeconds: number
  closeSeconds: number
}

function pairAndGroupByDay(
  schedule: ParsedEvent[],
  offsetSeconds: number
): Record<number, DayTimeRange[]> {
  const dayRanges: Record<number, DayTimeRange[]> = {}

  for (let i = 0; i < schedule.length; i++) {
    const event = schedule[i]
    if (event.eventType !== 'SESSION_OPEN') continue
    const closeEvent = schedule[i + 1]
    if (!closeEvent || closeEvent.eventType !== 'SESSION_CLOSE') continue

    const openSec =
      (((event.secondOfWeek + offsetSeconds) % SECONDS_PER_WEEK) + SECONDS_PER_WEEK) %
      SECONDS_PER_WEEK
    const closeSec =
      (((closeEvent.secondOfWeek + offsetSeconds) % SECONDS_PER_WEEK) + SECONDS_PER_WEEK) %
      SECONDS_PER_WEEK

    const openDay = Math.floor(openSec / SECONDS_PER_DAY)
    const openTimeInDay = openSec % SECONDS_PER_DAY
    let closeDay = Math.floor(closeSec / SECONDS_PER_DAY)
    let closeTimeInDay = closeSec % SECONDS_PER_DAY

    // Exact midnight belongs to end of previous day, not start of next
    if (closeTimeInDay === 0 && closeDay !== openDay) {
      closeDay = (closeDay - 1 + 7) % 7
      closeTimeInDay = SECONDS_PER_DAY
    }

    if (openDay === closeDay) {
      if (!dayRanges[openDay]) dayRanges[openDay] = []
      dayRanges[openDay].push({ openSeconds: openTimeInDay, closeSeconds: closeTimeInDay })
    } else {
      if (!dayRanges[closeDay]) dayRanges[closeDay] = []
      dayRanges[closeDay].push({ openSeconds: 0, closeSeconds: closeTimeInDay })

      if (!dayRanges[openDay]) dayRanges[openDay] = []
      dayRanges[openDay].push({ openSeconds: openTimeInDay, closeSeconds: SECONDS_PER_DAY })
    }
  }

  for (const day of Object.keys(dayRanges)) {
    dayRanges[Number(day)].sort((a, b) => a.openSeconds - b.openSeconds)
  }

  return dayRanges
}

function formatVolume(vol: string): string {
  if (vol === '+') return '+'
  const num = parseFloat(vol)
  return isNaN(num) ? vol : num.toFixed(2).replace(/\.?0+$/, '')
}

function unitsToLots(value: string, lotSize: number | null): string {
  if (value === 'Infinity' || value === '+') return '+'
  const num = parseFloat(value)
  if (isNaN(num) || !lotSize || lotSize === 0) return value
  return (num / lotSize).toString()
}

function formatTimeOfDay(seconds: number): string {
  if (seconds >= SECONDS_PER_DAY) return '00:00'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

function mergeRanges(ranges: DayTimeRange[]): DayTimeRange[] {
  if (ranges.length <= 1) return ranges
  const merged: DayTimeRange[] = [{ ...ranges[0] }]
  for (let i = 1; i < ranges.length; i++) {
    const prev = merged[merged.length - 1]
    const curr = ranges[i]
    if (curr.openSeconds <= prev.closeSeconds) {
      prev.closeSeconds = Math.max(prev.closeSeconds, curr.closeSeconds)
    } else {
      merged.push({ ...curr })
    }
  }
  return merged
}

interface TradingHoursRowProps {
  day: Day
  dayRanges: Record<number, DayTimeRange[]>
}

const TradingHoursRow: React.FC<TradingHoursRowProps> = ({ day, dayRanges }) => {
  const { t } = useTranslation()

  const dayLabel = t(`MarketInfo.days.${day}`)
  const dayIdx = DAY_TO_INDEX[day.toLowerCase()]
  const raw = dayRanges[dayIdx]

  if (!raw || raw.length === 0) {
    return (
      <Box flex direction='row' justify='space-between'>
        <Body2 color='text-med-em'>{dayLabel}</Body2>
        <Body2 color='text-danger-high-em'>{t('MarketInfo.marketClosed')}</Body2>
      </Box>
    )
  }

  const ranges = mergeRanges(raw)

  return (
    <Box flex direction='row' justify='space-between' align='flex-start'>
      <Body2 color='text-med-em'>{dayLabel}</Body2>
      <Box flex direction='column' align='flex-end'>
        {ranges.map((r, i) => (
          <Body2 key={i}>
            {formatTimeOfDay(r.openSeconds)} — {formatTimeOfDay(r.closeSeconds)}
          </Body2>
        ))}
      </Box>
    </Box>
  )
}

interface MarginTierRowProps {
  tier: DxTradeInstrumentMarginTier
  index: number
  array: DxTradeInstrumentMarginTier[]
  lotSize: number | null
}

const MarginTierRow: React.FC<MarginTierRowProps> = ({ tier, index, array, lotSize }) => {
  const { t } = useTranslation()

  const startRaw = index === 0 ? '0.01' : array[index - 1]?.endVolume ?? '0'
  const endRaw = tier.endVolume === 'Infinity' ? '+' : tier.endVolume ?? '+'

  const formattedStart = formatVolume(unitsToLots(startRaw, lotSize))
  const formattedEnd = formatVolume(unitsToLots(endRaw, lotSize))
  const isLastTier = formattedEnd === '+'

  const marginValue =
    tier.value != null ? `${(tier.value * 100).toFixed(2).replace(/\.?0+$/, '')}%` : '—'

  return (
    <Box flex direction='row' justify='space-between'>
      <Body2 color='text-med-em'>
        {isLastTier
          ? t('MarketInfo.lotsFrom', { from: formattedStart })
          : t('MarketInfo.lotsRange', { from: formattedStart, to: formattedEnd })}
      </Body2>
      <Body2>{marginValue}</Body2>
    </Box>
  )
}

export const MarketTabBody: React.FC<MarketTabProps> = ({ tradingInfo, isLoading }) => {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <Box px='x16' py='x16' flex direction='column' gap='x16'>
        <Loader />
      </Box>
    )
  }

  if (!tradingInfo) {
    return (
      <Box px='x16' py='x16' flex direction='column' gap='x16'>
        <Body2>{t('MarketInfo.noTradingInfo')}</Body2>
      </Box>
    )
  }

  const schedule = tradingInfo.tradingHours ? normalizeSchedule(tradingInfo.tradingHours) : []
  const dayRanges = pairAndGroupByDay(schedule, getLocalOffsetSeconds())

  const hasTiers = (tradingInfo.marginRate?.tiers?.length ?? 0) > 0

  return (
    <Box pb='x16' px='x16' flex direction='column' gap='x16'>
      <Box flex direction='column' gap='x8'>
        <Body1 medium>{t('MarketInfo.tradingInfo')}</Body1>

        <Box flex direction='row' justify='space-between'>
          <Body2 color='text-med-em'>{t('MarketInfo.lotSize')}</Body2>
          <Body2>{tradingInfo.lotSize?.toFixed(2) ?? '—'}</Body2>
        </Box>

        <Box flex direction='row' justify='space-between'>
          <Body2 color='text-med-em'>{t('MarketInfo.tradeSizeLots')}</Body2>
          <Body2>
            <span>{tradingInfo.minOrderSize?.toFixed(2) ?? '—'}</span>
            {' — '}
            <span>{tradingInfo.maxOrderSize?.toFixed(2) ?? '—'}</span>
          </Body2>
        </Box>

        <Box flex direction='row' justify='space-between'>
          <Body2 color='text-med-em'>{t('MarketInfo.tradeSize')}</Body2>
          <Body2>{tradingInfo.minOrderSize?.toFixed(2) ?? '—'}</Body2>
        </Box>

        <Box flex direction='row' justify='space-between'>
          <Body2 color='text-med-em'>{t('MarketInfo.stepsLots')}</Body2>
          <Body2>{tradingInfo.minOrderSizeIncrement?.toFixed(2) ?? '—'}</Body2>
        </Box>

        <Box flex direction='row' justify='space-between'>
          <Body2 color='text-med-em'>{t('MarketInfo.precision')}</Body2>
          <Body2>
            {tradingInfo.priceIncrement != null
              ? tradingInfo.priceIncrement.toFixed(10).replace(/\.?0+$/, '')
              : '—'}
          </Body2>
        </Box>
      </Box>

      <Box flex direction='column' gap='x8'>
        <Body1 medium>
          {t('MarketInfo.tradingHours', {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          })}
        </Body1>

        {DAYS_ORDER.map((day) => (
          <TradingHoursRow key={day} day={day} dayRanges={dayRanges} />
        ))}
      </Box>

      {hasTiers && (
        <Box flex direction='column' gap='x8'>
          <Body1 medium>{t('MarketInfo.marginRate')}</Body1>

          {tradingInfo.marginRate!.tiers!.map((tier, index, array) => (
            <MarginTierRow
              key={index}
              tier={tier}
              index={index}
              array={array}
              lotSize={tradingInfo.lotSize}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
